import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import cn from 'clsx';
import queryString from 'query-string';

import Link from '../../../../library/link/link';
import Typography from '../../../../library/typography/typography';
import ProductArticleStatusBanner from '../../../../components/product-article-status-banner/product-article-status-banner.one-dt';
import ProductAvailability from '../../../../components/product-availability/product-availability';
import ProductDescription from '../../../../components/product-description/product-description';
import ProductImage from '../../../../components/product-image/product-image';
import ProductRating from '../../../../components/product-rating/product-rating';
import ProductSecondaryFlags from '../../../../components/product-secondary-flags/product-secondary-flags';
import ProductSwatchSelection from '../../../../components/product-swatch-selection/product-swatch-selection';
import SignInToSave, {
  SignInToSaveVariant
} from '../../../../components/sign-in-to-save/sign-in-to-save';
import TireAttributeIcons from '../../../../components/tire-attribute-icons/tire-attribute-icons.one-dt';
import ViewOnVehicleButton from '../../../../components/view-on-vehicle-button/view-on-vehicle-button';
import WebOnlyFlag from '../../../../components/web-only-flag/web-only-flag';
import WinterTireBadge from '../../../../components/winter-tire-badge/winter-tire-badge';
import CompareCheckbox from '../../components/compare-checkbox/compare-checkbox';
import { WhiteAngleRegularIcon } from '../../../../shared/icons/icons';
import ProductListCardHighlight from '../product-list-card-highlight/product-list-card-highlight';
import ProductListCardPrice from '../product-list-card-price/product-list-card-price';

import {
  useCmsPageContentSlots,
  useContainerQuery,
  useFitment,
  useOptimisticData,
  useProductEntryDataLayerValues,
  useProductSwatchSelection,
  useTreadwellPreferences
} from '../../../../shared/hooks/hooks';

import { trackAction } from '../../../../shared/utils/analytics/analytics';
import { getShouldShowCompare } from '../../../../shared/utils/block-utils/block-utils';
import {
  getSignInPromo,
  isProductTrailerOrLawn
} from '../../../../shared/utils/cart/cart';
import {
  getAverageReviewRating,
  getNormalizedProductType,
  getNumberOfProductReviews,
  getPrioritizedArticleStatusProduct,
  getProductPromotions,
  getShouldShowProductArticleStatusBanner,
  getTireAttributeIcons,
  isCanonicalProduct,
  isWebOnlyProduct,
  isWindShieldWiperProduct,
  isWinterTire
} from '../../../../shared/utils/product/product';

import { ProductData } from '../../../../shared/types/graphql-types';
import {
  PRODUCT_TYPES,
  TIRE_ATTRIBUTES
} from '../../../../shared/constants/products';
import { CURRENT_SEARCH_OPTIONS } from '../../../../shared/constants/search-types';

import styles from './product-list-card-details.scss';

interface ProductListCardDetailsProps {
  header?: string;
  hideAvailability?: boolean;
  hideCompare?: boolean;
  hideHighlight?: boolean;
  hideImage?: boolean;
  hideProductSize?: boolean;
  hideReviews?: boolean;
  hideSignInBanner?: boolean;
  hideViewOnVehicleAddToCart?: boolean;
  hideViewOnVehicleFilters?: boolean;
  onCheckInventoryClick: () => void;
  onViewProductClick: () => void;
  product?: ProductData;
  productDetailUrl: string;
  productSet?: Array<ProductData>;
  productSetQuantity?: Array<number>;
  quantity: number;
  rearQuantity?: number;
  showPriceInDetails?: boolean;
  totalQuantity?: number;
  wiperContainsMsg?: string;
}

function ProductListCardDetails(props: ProductListCardDetailsProps) {
  const {
    header = '',
    hideAvailability = false,
    hideCompare = false,
    hideHighlight = false,
    hideImage = false,
    hideProductSize = false,
    hideReviews = false,
    hideSignInBanner = false,
    hideViewOnVehicleAddToCart = false,
    hideViewOnVehicleFilters = false,
    onCheckInventoryClick,
    onViewProductClick,
    product,
    productDetailUrl,
    productSet,
    productSetQuantity,
    quantity,
    rearQuantity,
    showPriceInDetails = false,
    totalQuantity,
    wiperContainsMsg
  } = props;

  const {
    articleStatus,
    brand,
    code,
    gbb,
    name,
    rearProduct,
    swatches,
    vendorFinish
  } = product || {};

  const { selectedFitment } = useFitment();

  const { isTreadwellFlow, treadwellPreferences } = useTreadwellPreferences();
  const { getContentSlot: getPillarPromoSlot } =
    useCmsPageContentSlots('pillar-promo');
  const badgeIcon = getPillarPromoSlot('badge-icon-url');
  const plpOverlayUrl = getPillarPromoSlot('plp-overlay-url');
  const productPromotions = getProductPromotions(product);
  const hasSignInPromo = Boolean(getSignInPromo(productPromotions));
  const optimisticData = useOptimisticData();
  const { isMinSm } = useContainerQuery();
  const isCanonical = isCanonicalProduct(product);
  const isFleetPreferred =
    product?.isFleetPreferred || product?.rearProduct?.isFleetPreferred;
  const isWinterOnly = isWinterTire(product);
  const isWebOnly = isWebOnlyProduct(product);
  const { search } = useLocation();
  const { currentSearchOption } = queryString.parse(search);
  const isTreadwellSearchOption =
    currentSearchOption === CURRENT_SEARCH_OPTIONS.TREADWELL;

  const isStaggered = Boolean(rearProduct);
  const productSize = isWindShieldWiperProduct(product)
    ? `${product?.size}"`
    : product?.size;
  const size = isCanonicalProduct(product) ? undefined : productSize;
  const productType = getNormalizedProductType(product);
  const accessoryImgSize = isMinSm ? 240 : 150;
  const tireOrWheelProductImgSize = 300;

  const numberOfProductReview = getNumberOfProductReviews(product);
  const averageReviewRating = getAverageReviewRating(product);
  const shouldShowCompare = getShouldShowCompare(articleStatus);

  const { saveSelectedSwatch, selectedSwatch } = useProductSwatchSelection(
    code,
    swatches
  );

  const { getProductEntryDataLayerValues } = useProductEntryDataLayerValues();

  const [productCode, setProductCode] = useState(code);

  const articleStatusProduct = getPrioritizedArticleStatusProduct(
    product,
    product?.rearProduct
  );
  const shouldShowProductArticleStatusBanner =
    getShouldShowProductArticleStatusBanner(articleStatusProduct);

  let imgClassName = 'product-img';

  if (productType === PRODUCT_TYPES.WHEELS) {
    imgClassName = 'product-img-wheel';
  } else if (productType === PRODUCT_TYPES.TIRES) {
    imgClassName = 'product-img-tire';
  }

  const isWinterTreadwellFlow = Boolean(treadwellPreferences?.showWinterTires);

  function setOptimisticProductData() {
    optimisticData.write({ product });
    onViewProductClick?.();
  }

  function onSwatchChange(swatch) {
    saveSelectedSwatch(swatch);
    setProductCode(swatch?.productCode);
  }

  function handleReadReviewsClick() {
    trackAction(
      'product_read_reviews_click',
      getProductEntryDataLayerValues(product)
    );
  }

  return (
    <div className={styles['container']}>
      {!isMinSm &&
        process.env.__FF_PLP_CONDENSED_CARDS__ === 'true' &&
        !hideHighlight && (
          <ProductListCardHighlight
            hideTreadwellRank={!isTreadwellFlow}
            product={product}
          />
        )}
      {process.env.__FF_PROMOTION_SIGN_IN_BANNER__ === 'true' &&
        !isMinSm &&
        !hideSignInBanner && (
          <SignInToSave
            badgeIcon={badgeIcon}
            className="sign-in-to-save-plp"
            primaryColor={getPillarPromoSlot('primary-color')}
            promos={productPromotions}
            secondaryColor={getPillarPromoSlot('secondary-color')}
            variant={SignInToSaveVariant.LINK}
          />
        )}
      {!hideImage && (
        <div className={styles['img-col']}>
          {(isMinSm || process.env.__FF_PLP_CONDENSED_CARDS__ !== 'true') &&
            !hideHighlight && (
              <ProductListCardHighlight
                hideTreadwellRank={!isTreadwellFlow}
                hideWebOnly
                product={product}
              />
            )}

          {process.env.__FF_FLEET_ACCOUNT__ === 'true' && (
            <ProductSecondaryFlags
              hideWebOnly={
                !isMinSm
                  ? process.env.__FF_PLP_CONDENSED_CARDS__ === 'true'
                  : !isFleetPreferred
              }
              product={product}
            />
          )}

          {!isMinSm &&
            process.env.__FF_PLP_CONDENSED_CARDS__ === 'true' &&
            shouldShowCompare &&
            !hideCompare &&
            !isCanonical &&
            productType === PRODUCT_TYPES.TIRES && (
              <CompareCheckbox
                hideSelectMoreText
                product={product}
                quantity={quantity}
                rearQuantity={rearQuantity}
              />
            )}

          <ProductImage
            className={styles[imgClassName]}
            height={
              productType === PRODUCT_TYPES.ACCESSORIES
                ? accessoryImgSize
                : tireOrWheelProductImgSize
            }
            imageFormatType="PRODUCT_MEDIUM"
            imageUrl={selectedSwatch?.linkUrl}
            linkUrl={productDetailUrl}
            onClick={setOptimisticProductData}
            product={product}
            width={
              productType === PRODUCT_TYPES.ACCESSORIES
                ? accessoryImgSize
                : tireOrWheelProductImgSize
            }
          />

          {isWebOnly &&
            !isFleetPreferred &&
            (isMinSm || process.env.__FF_PLP_CONDENSED_CARDS__ !== 'true') && (
              <WebOnlyFlag className="product-list-card-details__web-only-flag" />
            )}

          {isWinterTreadwellFlow && isTreadwellSearchOption && (
            <div className={styles['winter-tire-badge']}>
              <WinterTireBadge isWinterOnly={isWinterOnly} />
            </div>
          )}

          {(isMinSm || process.env.__FF_PLP_CONDENSED_CARDS__ !== 'true') &&
            !(plpOverlayUrl && !hideSignInBanner) && (
              <WhiteAngleRegularIcon className={styles['icon']} />
            )}
        </div>
      )}
      {process.env.__FF_PROMOTION_SIGN_IN_BANNER__ === 'true' &&
        isMinSm &&
        hasSignInPromo &&
        !hideSignInBanner &&
        plpOverlayUrl && (
          <div className={styles['plp-overlay-wrapper']}>
            <img alt="" className={styles['plp-overlay']} src={plpOverlayUrl} />
          </div>
        )}
      {process.env.__FF_PROMOTION_SIGN_IN_BANNER__ === 'true' &&
        isMinSm &&
        !hideSignInBanner && (
          <SignInToSave
            badgeIcon={badgeIcon}
            primaryColor={getPillarPromoSlot('primary-color')}
            promos={productPromotions}
            secondaryColor={getPillarPromoSlot('secondary-color')}
            variant={SignInToSaveVariant.LINK}
          />
        )}
      {isCanonical &&
        process.env.__FF_PLP_CONDENSED_CARDS__ === 'true' &&
        isMinSm && (
          <Typography
            className={styles['exact-price-msg']}
            size="small"
            tag="div"
          >
            {selectedFitment
              ? 'View details for price & inventory'
              : !isProductTrailerOrLawn(product) && (
                  <>
                    <Link
                      className={styles['vehicle-link']}
                      size="small"
                      to="#/fitment/vehicle"
                    >
                      enter vehicle
                    </Link>{' '}
                    for exact price & inventory
                  </>
                )}
          </Typography>
        )}
      <div className={styles['desc-col']}>
        {header && (
          <Typography
            className={cn(
              styles['header'],
              'product-list-card-details__header'
            )}
            tag="div"
          >
            {header}
          </Typography>
        )}
        <div
          className={cn(
            isStaggered
              ? styles['product-description-staggered']
              : styles['product-description'],
            'product-list-card-details__product-description'
          )}
        >
          <div className={styles['product-description-firstline']}>
            <ProductDescription brand={brand} />
            {(isMinSm || process.env.__FF_PLP_CONDENSED_CARDS__ !== 'true') &&
              shouldShowCompare &&
              !hideCompare &&
              !isCanonical &&
              productType === PRODUCT_TYPES.TIRES && (
                <CompareCheckbox
                  hideSelectMoreText
                  product={product}
                  quantity={quantity}
                  rearQuantity={rearQuantity}
                />
              )}
          </div>

          <ProductDescription
            link={productDetailUrl}
            name={name}
            onClick={setOptimisticProductData}
            productType={productType}
            size={isStaggered || hideProductSize ? undefined : size}
          />
        </div>

        {!isMinSm && wiperContainsMsg && (
          <div className={styles['product-contains']}>{wiperContainsMsg}</div>
        )}

        {!isMinSm &&
          isCanonical &&
          process.env.__FF_PLP_CONDENSED_CARDS__ === 'true' && (
            <Typography
              className={styles['canonical-wiper-exact-price-msg']}
              size="small"
              tag="div"
            >
              {selectedFitment
                ? 'View details for price & inventory'
                : !isProductTrailerOrLawn(product) && (
                    <>
                      <Link
                        className={styles['vehicle-link']}
                        size="small"
                        to="#/fitment/vehicle"
                      >
                        enter vehicle
                      </Link>{' '}
                      for exact price & inventory
                    </>
                  )}
            </Typography>
          )}

        {showPriceInDetails && !isWindShieldWiperProduct(product) && (
          <ProductListCardPrice
            hidePercentageSaved
            product={product}
            quantity={quantity}
            totalQuantity={totalQuantity}
          />
        )}

        {!hideReviews && productType === PRODUCT_TYPES.TIRES && (
          <ProductRating
            avgReviewRating={averageReviewRating}
            hideRating
            link={`${productDetailUrl}#read-reviews`}
            linkText={`${numberOfProductReview} Reviews`}
            numberOfReviews={numberOfProductReview}
            onReadReviewClick={handleReadReviewsClick}
            productCode={product?.code}
            rearProductCode={rearProduct?.code}
            showCount={false}
          />
        )}

        {productType === PRODUCT_TYPES.TIRES && (
          <div className={styles['gbb-and-badges']}>
            {gbb && (
              <>
                <ProductDescription gbb={gbb} showGBBTooltip />
                <ellipse className={styles['ellipse']} />
              </>
            )}
            <TireAttributeIcons
              icons={[
                ...getTireAttributeIcons(product),
                ...(Number(
                  product?.recommendationData?.stopping?.rollingResistanceRating
                ) >= 4
                  ? [TIRE_ATTRIBUTES.TREADWELL_FUEL_EFFICIENT]
                  : [])
              ]}
              link={`${productDetailUrl}#tire-attribute-icons`}
            />
          </div>
        )}

        {(isMinSm || process.env.__FF_PLP_CONDENSED_CARDS__ !== 'true') &&
          !hideAvailability &&
          product && (
            <div className={styles['product-availability']}>
              {shouldShowProductArticleStatusBanner ? (
                <ProductArticleStatusBanner
                  product={articleStatusProduct}
                  showBoxDesign={
                    process.env.__FF_PLP_CONDENSED_CARDS__ === 'true'
                  }
                />
              ) : (
                <ProductAvailability
                  onCheckInventoryClick={onCheckInventoryClick}
                  product={product}
                  productSet={productSet}
                  productSetQuantity={productSetQuantity}
                  quantity={quantity}
                  rearProduct={product?.rearProduct}
                  rearQuantity={rearQuantity}
                  showBoxDesign={
                    process.env.__FF_PLP_CONDENSED_CARDS__ === 'true'
                  }
                />
              )}
            </div>
          )}

        {(isMinSm || process.env.__FF_PLP_CONDENSED_CARDS__ !== 'true') &&
          productType === PRODUCT_TYPES.WHEELS && (
            <>
              {productCode && (
                <ProductSwatchSelection
                  onSwatchChange={onSwatchChange}
                  productCode={productCode}
                  productDetailsUrl={productDetailUrl}
                  productFinish={vendorFinish}
                  swatches={swatches}
                />
              )}

              <div className={styles['view-on-vehicle']}>
                {product?.isViewableOnVehicle && (
                  <ViewOnVehicleButton
                    hideAddProduct={hideViewOnVehicleAddToCart}
                    hideFilters={hideViewOnVehicleFilters}
                    isCanonical={isCanonical}
                    onClick={() =>
                      trackAction(
                        'product_list_view_on_vehicle',
                        getProductEntryDataLayerValues(product)
                      )
                    }
                    product={{
                      ...product,
                      url: productDetailUrl || product?.url
                    }}
                    selectedSwatch={selectedSwatch}
                    shouldSkipQuery={hideViewOnVehicleFilters}
                  />
                )}
              </div>
            </>
          )}
      </div>

      {productType === PRODUCT_TYPES.WHEELS &&
        !isMinSm &&
        process.env.__FF_PLP_CONDENSED_CARDS__ === 'true' && (
          <>
            {productCode && (
              <ProductSwatchSelection
                onSwatchChange={onSwatchChange}
                productCode={productCode}
                productDetailsUrl={productDetailUrl}
                productFinish={vendorFinish}
                swatches={swatches}
              />
            )}

            <div className={styles['view-on-vehicle']}>
              {product?.isViewableOnVehicle && (
                <ViewOnVehicleButton
                  hideAddProduct={hideViewOnVehicleAddToCart}
                  hideFilters={hideViewOnVehicleFilters}
                  isCanonical={isCanonical}
                  onClick={() =>
                    trackAction(
                      'product_list_view_on_vehicle',
                      getProductEntryDataLayerValues(product)
                    )
                  }
                  product={{
                    ...product,
                    url: productDetailUrl || product?.url
                  }}
                  selectedSwatch={selectedSwatch}
                  shouldSkipQuery={hideViewOnVehicleFilters}
                />
              )}
            </div>
          </>
        )}

      {!isMinSm && showPriceInDetails && isWindShieldWiperProduct(product) && (
        <div className={styles['canonical-wiper-price']}>
          <ProductListCardPrice
            hidePercentageSaved
            product={product}
            quantity={quantity}
            totalQuantity={totalQuantity}
          />
        </div>
      )}

      {!isMinSm &&
        process.env.__FF_PLP_CONDENSED_CARDS__ === 'true' &&
        !hideAvailability &&
        product && (
          <div className={styles['product-availability']}>
            {shouldShowProductArticleStatusBanner ? (
              <ProductArticleStatusBanner
                product={articleStatusProduct}
                showBoxDesign={
                  process.env.__FF_PLP_CONDENSED_CARDS__ === 'true'
                }
              />
            ) : (
              <ProductAvailability
                onCheckInventoryClick={onCheckInventoryClick}
                product={product}
                productSet={productSet}
                productSetQuantity={productSetQuantity}
                quantity={quantity}
                rearProduct={product?.rearProduct}
                rearQuantity={rearQuantity}
                showBoxDesign={
                  process.env.__FF_PLP_CONDENSED_CARDS__ === 'true'
                }
              />
            )}
          </div>
        )}
    </div>
  );
}

export default ProductListCardDetails;
